import { createSelector } from 'reselect';

import { NUMBER_MONTHS_IN_YEAR } from 'constants/date';
import { calculateNextAmountDueDate } from 'utils/impounds';

import { selectImpoundTypes } from 'store/system/selectors';

import { selectExtendedBorrowers } from './borrowers.selectors';
import { selectSortedCountiesByStates } from './counties.selectors';
import { selectLoanApplication } from './loanApplication.selectors';

const selectProperties = createSelector(selectLoanApplication, ({ properties }) => properties);

export const selectPropertyValues = createSelector(selectProperties, Object.values);

export const selectSubjectProperty = createSelector(
  selectLoanApplication,
  ({ subjectProperty }) => subjectProperty,
);

export const selectExtendedSubjectProperty = createSelector(
  [
    selectPropertyValues,
    selectSubjectProperty,
    selectExtendedBorrowers,
    selectSortedCountiesByStates,
    selectImpoundTypes,
  ],
  (properties, subjectProperty, extendedBorrowers, counties, impoundTypes) => {
    const { propertyValue } = properties?.find(({ isSubjectProperty }) => isSubjectProperty) || {};
    const county = subjectProperty?.address?.county;
    const state = subjectProperty?.address?.state;

    const subjectPropertyBorrowers = extendedBorrowers?.map(b => ({
      ...b,
      isOwner: !!subjectProperty?.owners?.includes(b.borrowerId),
    }));

    subjectProperty = {
      ...subjectProperty,
      address: {
        ...subjectProperty?.address,
        county: counties[state]?.find(({ value }) =>
          value.toLowerCase().startsWith(county?.toLowerCase()),
        )?.value,
      },
    };
    //TODO: remove County find when Apply and AdminPanel start using the same County format

    return {
      ...(subjectProperty || {}),
      // TODO: When "Current Market Value" field will not needed - "propertyValue" can be removed
      propertyValue,
      subjectPropertyBorrowers,

      supplementalInsurances: (subjectProperty.supplementalInsurances || []).map(i => ({
        ...i,
        monthlyAmount: i.annualAmount / NUMBER_MONTHS_IN_YEAR,
        impoundTypeName: impoundTypes[i.impoundTypeId]?.name,
      })),
      propertyTaxes: (subjectProperty.propertyTaxes || []).map(i => {
        const nextAmountDueDate = calculateNextAmountDueDate(i);

        return {
          ...i,
          monthlyAmount: i.annualAmount / NUMBER_MONTHS_IN_YEAR,
          overrodeMonthlyAmount: i.overrodeAnnualAmount / NUMBER_MONTHS_IN_YEAR,
          impoundTypeName: impoundTypes[i.impoundTypeId]?.name,
          nextAmountDue: nextAmountDueDate.amountDue,
          nextDueDate: nextAmountDueDate.dueDate,
          qualifyingForMonthlyAmount: !!i.overrodeAnnualAmount,
        };
      }),
      hasPropertyTaxes: !!subjectProperty.propertyTaxes?.length,
      hasSupplementalInsurances: !!subjectProperty.supplementalInsurances?.length,
      isNewConstruction: !!subjectProperty.completionDate,
    };
  },
);

export const selectPropertyLinkedLiabilityIds = createSelector(selectPropertyValues, properties =>
  properties.reduce((acc, { firstMortgageId, secondMortgageId }) => {
    const ids = [firstMortgageId, secondMortgageId].filter(Boolean);

    if (ids.length) {
      acc.push(...ids);
    }

    return acc;
  }, []),
);
